import {
  checkPdCoherence,
  MONOCULAR_PD_RANGE_TENTHS,
  parsePdToTenths,
  TOTAL_PD_RANGE_TENTHS,
} from '@optiflow/shared';
import {
  isMultifocalLensType,
  MAX_OPTICAL_JOBS_PER_ORDER,
  type CustomerMeasurementSummary,
  type MeasurementSource,
  type EyePrescriptionInput,
  type FrameDetails,
  type OpticalJobInput,
  type OpticalPrescriptionInput,
  type PdType,
  type PrescriptionLensDetails,
  type ProductSummary,
  type ProductType,
  type ProductVariantSummary,
} from '@optiflow/types';
import {
  EMPTY_EYE,
  validateEye,
  type EyeField,
  type EyeForm,
  type EyeSide,
  type FieldError,
  type FormErrors,
} from '@/modules/medical/examinationForm';
import type {
  CounterSaleLine,
  CounterSalePayload,
  Tender,
  ValidateBasketPayload,
} from '@/modules/sales/api/sales.api';
import { isPrescribablePd } from '@/modules/medical/pdEligibility';
import { creditTermsInput, type CreditTermsDraft } from '@/modules/sales/creditTerms';

/**
 * The optical order builder's draft: what the user has chosen so far, in memory only.
 *
 * Nothing here is written to browser storage, the address or a log; a refresh loses it, which is accepted. The draft holds identifiers and what was typed. Prices it holds are only what the screen showed, sent back as expectations for the server to check — never as prices. Pair numbers are labels for the screen; the server numbers the jobs.
 */

export const BUILDER_STEPS = ['prescription', 'frame', 'lenses', 'review', 'payment'] as const;
export type BuilderStep = (typeof BUILDER_STEPS)[number];

/** The steps taken one pair at a time. */
export const PAIR_STEPS: readonly BuilderStep[] = ['prescription', 'frame', 'lenses'];

export type PdMode = 'BINOCULAR' | 'MONOCULAR';

/** What the pair's glasses are for. A screen aid only: it presets the PD type and guides the lens choice, and is never sent. */
export const PAIR_USAGES = ['DISTANCE', 'NEAR', 'DISTANCE_NEAR', 'MULTIFOCAL'] as const;
export type PairUsage = (typeof PAIR_USAGES)[number];

/** The PD type a usage presets on an external prescription; multifocal and "distance + near" leave it as it is. */
const USAGE_PD_TYPE: Partial<Record<PairUsage, PdType>> = { DISTANCE: 'DISTANCE', NEAR: 'NEAR' };

export interface ExternalPrescriptionForm {
  right: EyeForm;
  left: EyeForm;
  pdMode: PdMode;
  totalPdMm: string;
  rightPdMm: string;
  leftPdMm: string;
  pdType: PdType | '';
  externalReference: string;
  prescriberName: string;
  /** `YYYY-MM-DD`, or empty. */
  prescribedOn: string;
  /** `YYYY-MM-DD`, or empty. */
  expiresOn: string;
  /** The saved measurement the PD was filled from, unchanged; null once the PD is typed or edited. Only this sends a measurement reference. */
  pdSource: SavedPdSource | null;
  /** True once someone typed or edited the PD: it is never refilled on its own, only by an explicit "use saved PD". */
  pdManual: boolean;
}

/** Where a filled PD came from, as the screen names it: a date and how it was measured, never an id shown to anyone. */
export interface SavedPdSource {
  measurementId: string;
  measuredAt: string;
  source: MeasurementSource;
}

export interface PairPrescription {
  /** Null until the user chooses: a clinical prescription is never picked for them. */
  source: 'CLINICAL' | 'EXTERNAL' | null;
  clinicalPrescriptionId: string | null;
  /** True once a chosen clinical prescription turned out unusable, until another is chosen. */
  clinicalUnavailable: boolean;
  /** Kept while the clinical source is chosen, so switching back loses nothing typed. Sent only for the external source. */
  external: ExternalPrescriptionForm;
}

export interface SelectedFrame {
  productId: string;
  productType: ProductType;
  variantId: string;
  productName: string;
  variantName: string | null;
  sku: string;
  brandName: string | null;
  details: FrameDetails | null;
  /** The price shown when it was chosen: checked by the server as an expectation, never charged. */
  seenPriceMinor: number | null;
  /** The opaque selection token of a frame recommended for this customer, set only by a recommendation hand-off. Checkout verifies it; nothing else trusts it. */
  recommendationToken: string | null;
}

export interface SelectedLens {
  productId: string;
  variantId: string;
  productName: string;
  variantName: string | null;
  sku: string;
  brandName: string | null;
  details: PrescriptionLensDetails;
  seenPriceMinor: number | null;
}

export interface PairDraft {
  /** The screen's handle for the pair; never sent. */
  id: string;
  /** Request-local keys the checkout names this pair's lines by. Fixed for the pair's life, so a retried confirmation sends the same body. */
  lineKeys: { frame: string; right: string; left: string };
  prescription: PairPrescription;
  frame: SelectedFrame | null;
  lenses: { right: SelectedLens | null; left: SelectedLens | null };
  /** Millimetres as typed. */
  fitting: { right: string; left: string };
  /** Empty for "choose later". */
  labId: string;
  /** True once this pair's recommended frame was changed, removed or stripped of its recommendation: the screen says the recommendation no longer applies, and nothing brings it back. */
  droppedRecommendation: boolean;
  /** Null until the user says what the glasses are for. */
  usage: PairUsage | null;
  /** One lens chosen for both eyes. The order still carries one lens line per eye. */
  sameLens: boolean;
}

export interface BuilderDraft {
  pairs: PairDraft[];
  activePairId: string;
  step: BuilderStep;
  /** Anything chosen or typed: the only thing the leave warning asks about. */
  touched: boolean;
  tenders: Tender[];
  /** Null: the balance, if any, is left owing on the order. Set: it becomes a payment plan. */
  credit: CreditTermsDraft | null;
}

/** What a recommendation hand-off gives the builder: one exact frame, with its selection token, for the first pair. Never a price. */
export interface BuilderSeed {
  frame?: Omit<SelectedFrame, 'seenPriceMinor'> & { seenPriceMinor?: number | null };
}

export const EMPTY_EXTERNAL: ExternalPrescriptionForm = {
  right: EMPTY_EYE,
  left: EMPTY_EYE,
  pdMode: 'BINOCULAR',
  totalPdMm: '',
  rightPdMm: '',
  leftPdMm: '',
  pdType: 'DISTANCE',
  externalReference: '',
  prescriberName: '',
  prescribedOn: '',
  expiresOn: '',
  pdSource: null,
  pdManual: false,
};

const newId = () => crypto.randomUUID();

export function newPair(): PairDraft {
  return {
    id: newId(),
    lineKeys: { frame: newId(), right: newId(), left: newId() },
    prescription: {
      source: null,
      clinicalPrescriptionId: null,
      clinicalUnavailable: false,
      external: EMPTY_EXTERNAL,
    },
    frame: null,
    lenses: { right: null, left: null },
    fitting: { right: '', left: '' },
    labId: '',
    droppedRecommendation: false,
    usage: null,
    sameLens: true,
  };
}

/** A frame as the builder holds a chosen one. A token is given only by a recommendation hand-off; a frame chosen on the screen never carries one. */
export function frameChoice(
  variant: ProductVariantSummary,
  product: Pick<ProductSummary, 'id' | 'type' | 'name' | 'brandName'>,
  seenPriceMinor: number | null,
  recommendationToken: string | null = null,
): SelectedFrame {
  return {
    productId: product.id,
    productType: product.type,
    variantId: variant.id,
    productName: product.name,
    variantName: variant.variantName,
    sku: variant.sku,
    brandName: product.brandName,
    details: variant.frame,
    seenPriceMinor,
    recommendationToken,
  };
}

export function createDraft(seed?: BuilderSeed): BuilderDraft {
  const first = newPair();
  if (seed?.frame) first.frame = { seenPriceMinor: null, ...seed.frame };
  return {
    pairs: [first],
    activePairId: first.id,
    step: 'prescription',
    touched: Boolean(seed?.frame),
    tenders: [],
    credit: null,
  };
}

// ---------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------

export type BuilderAction =
  | { type: 'goTo'; step: BuilderStep; pairId?: string }
  | { type: 'selectPair'; pairId: string }
  | { type: 'chooseSource'; pairId: string; source: 'CLINICAL' | 'EXTERNAL' }
  | { type: 'preferExternal'; pairIds: string[] }
  | { type: 'setUsage'; pairId: string; usage: PairUsage }
  | { type: 'splitDistanceNear'; pairId: string }
  | { type: 'setSameLens'; pairId: string; on: boolean }
  | {
      type: 'applySavedPd';
      pairId: string;
      measurement: CustomerMeasurementSummary;
      byUser: boolean;
    }
  | { type: 'clearSavedPd'; pairId: string }
  | { type: 'chooseClinical'; pairId: string; clinicalPrescriptionId: string }
  | { type: 'clinicalUnavailable'; pairIds: string[] }
  | { type: 'editExternal'; pairId: string; patch: Partial<ExternalPrescriptionForm> }
  | { type: 'copyPrescription'; fromPairId: string; toPairId: string }
  | { type: 'setFrame'; pairId: string; frame: SelectedFrame | null }
  | { type: 'removeRecommendation'; pairId: string }
  | { type: 'setLens'; pairId: string; eye: EyeSide; lens: SelectedLens | null }
  | { type: 'copyRightLensToLeft'; pairId: string }
  | { type: 'setFitting'; pairId: string; eye: EyeSide; value: string }
  | { type: 'setLab'; pairId: string; labId: string }
  | { type: 'addPair' }
  | { type: 'removePair'; pairId: string }
  | { type: 'acceptPrices'; prices: Record<string, number> }
  | { type: 'setTenders'; tenders: Tender[] }
  | { type: 'setCredit'; credit: CreditTermsDraft | null };

function updatePair(
  draft: BuilderDraft,
  pairId: string,
  change: (pair: PairDraft) => PairDraft,
): BuilderDraft {
  return {
    ...draft,
    touched: true,
    pairs: draft.pairs.map((pair) => (pair.id === pairId ? change(pair) : pair)),
  };
}

export function builderReducer(draft: BuilderDraft, action: BuilderAction): BuilderDraft {
  switch (action.type) {
    case 'goTo':
      return {
        ...draft,
        step: action.step,
        activePairId:
          action.pairId && draft.pairs.some((pair) => pair.id === action.pairId)
            ? action.pairId
            : draft.activePairId,
      };
    case 'selectPair':
      return draft.pairs.some((pair) => pair.id === action.pairId)
        ? { ...draft, activePairId: action.pairId }
        : draft;
    case 'chooseSource':
      return updatePair(draft, action.pairId, (pair) => ({
        ...pair,
        prescription: { ...pair.prescription, source: action.source },
      }));
    case 'preferExternal':
      // The customer has no usable prescription from this shop: external is the only way forward, so it is preselected. Not a change the user made, so the leave warning does not count it.
      return {
        ...draft,
        pairs: draft.pairs.map((pair) =>
          action.pairIds.includes(pair.id) && pair.prescription.source === null
            ? { ...pair, prescription: { ...pair.prescription, source: 'EXTERNAL' } }
            : pair,
        ),
      };
    case 'setUsage':
      return updatePair(draft, action.pairId, (pair) => {
        const pdType = USAGE_PD_TYPE[action.usage];
        return {
          ...pair,
          usage: action.usage,
          prescription: pdType
            ? {
                ...pair.prescription,
                external: { ...pair.prescription.external, pdType },
              }
            : pair.prescription,
        };
      });
    case 'splitDistanceNear': {
      const index = draft.pairs.findIndex((pair) => pair.id === action.pairId);
      const from = draft.pairs[index];
      if (!from || draft.pairs.length >= MAX_OPTICAL_JOBS_PER_ORDER) return draft;
      const distance: PairDraft = {
        ...from,
        usage: 'DISTANCE',
        prescription: {
          ...from.prescription,
          external: { ...from.prescription.external, pdType: 'DISTANCE' },
        },
      };
      // The near pair takes the same eyes and prescription details; its PD starts empty because a distance PD is not a near PD.
      const near: PairDraft = {
        ...newPair(),
        usage: 'NEAR',
        prescription: {
          ...from.prescription,
          clinicalUnavailable: false,
          external: {
            ...from.prescription.external,
            pdType: 'NEAR',
            totalPdMm: '',
            rightPdMm: '',
            leftPdMm: '',
            pdSource: null,
            pdManual: false,
          },
        },
      };
      const pairs = [...draft.pairs];
      pairs.splice(index, 1, distance, near);
      return { ...draft, touched: true, pairs };
    }
    case 'setSameLens':
      return updatePair(draft, action.pairId, (pair) => {
        if (!action.on) return { ...pair, sameLens: false };
        const { right, left } = pair.lenses;
        // Two different lenses are never merged here; the screen asks first and copies the right eye's lens explicitly.
        if (right && left && right.variantId !== left.variantId) return pair;
        const lens = right ?? left;
        return { ...pair, sameLens: true, lenses: { right: lens, left: lens } };
      });
    case 'chooseClinical':
      return updatePair(draft, action.pairId, (pair) => ({
        ...pair,
        prescription: {
          ...pair.prescription,
          source: 'CLINICAL',
          clinicalPrescriptionId: action.clinicalPrescriptionId,
          clinicalUnavailable: false,
        },
      }));
    case 'clinicalUnavailable':
      // The pair keeps everything else; only the prescription that stopped being usable is let go, and nothing replaces it.
      return {
        ...draft,
        pairs: draft.pairs.map((pair) =>
          action.pairIds.includes(pair.id) && pair.prescription.source === 'CLINICAL'
            ? {
                ...pair,
                prescription: {
                  ...pair.prescription,
                  clinicalPrescriptionId: null,
                  clinicalUnavailable: true,
                },
              }
            : pair,
        ),
      };
    case 'editExternal':
      return updatePair(draft, action.pairId, (pair) => {
        // Any change to the PD itself makes it typed by hand: the measurement reference goes, and nothing refills it on its own.
        const touchesPd = PD_VALUE_FIELDS.some((field) => field in action.patch);
        return {
          ...pair,
          prescription: {
            ...pair.prescription,
            external: {
              ...pair.prescription.external,
              ...action.patch,
              ...(touchesPd ? { pdSource: null, pdManual: true } : {}),
            },
          },
        };
      });
    case 'applySavedPd': {
      const filled = savedPdFields(action.measurement);
      if (!filled) return draft;
      const change = (pair: PairDraft): PairDraft => ({
        ...pair,
        prescription: {
          ...pair.prescription,
          external: { ...pair.prescription.external, ...filled },
        },
      });
      // Filled on arrival is not a change the user made; "use saved PD" is.
      return action.byUser
        ? updatePair(draft, action.pairId, change)
        : {
            ...draft,
            pairs: draft.pairs.map((pair) => (pair.id === action.pairId ? change(pair) : pair)),
          };
    }
    case 'clearSavedPd':
      // A filled PD whose type no longer has a saved measurement is emptied, never carried over as another type's PD.
      return {
        ...draft,
        pairs: draft.pairs.map((pair) =>
          pair.id === action.pairId && pair.prescription.external.pdSource
            ? {
                ...pair,
                prescription: {
                  ...pair.prescription,
                  external: {
                    ...pair.prescription.external,
                    totalPdMm: '',
                    rightPdMm: '',
                    leftPdMm: '',
                    pdSource: null,
                  },
                },
              }
            : pair,
        ),
      };
    case 'copyPrescription': {
      const from = draft.pairs.find((pair) => pair.id === action.fromPairId);
      if (!from) return draft;
      return updatePair(draft, action.toPairId, (pair) => ({
        ...pair,
        prescription: { ...from.prescription, clinicalUnavailable: false },
      }));
    }
    case 'setFrame':
      return updatePair(draft, action.pairId, (pair) => {
        // A recommendation belongs to the exact frame it was signed for: any other frame, or none, lets it go for good.
        const carried = pair.frame?.recommendationToken ?? null;
        const kept = carried !== null && action.frame?.recommendationToken === carried;
        return {
          ...pair,
          frame: action.frame,
          droppedRecommendation: pair.droppedRecommendation || (carried !== null && !kept),
        };
      });
    case 'removeRecommendation':
      return updatePair(draft, action.pairId, (pair) =>
        pair.frame?.recommendationToken
          ? {
              ...pair,
              frame: { ...pair.frame, recommendationToken: null },
              droppedRecommendation: true,
            }
          : pair,
      );
    case 'setLens':
      return updatePair(draft, action.pairId, (pair) => ({
        ...pair,
        lenses: sameLensActive(pair)
          ? { right: action.lens, left: action.lens }
          : { ...pair.lenses, [action.eye]: action.lens },
      }));
    case 'copyRightLensToLeft':
      return updatePair(draft, action.pairId, (pair) => ({
        ...pair,
        lenses: { ...pair.lenses, left: pair.lenses.right },
      }));
    case 'setFitting':
      return updatePair(draft, action.pairId, (pair) => ({
        ...pair,
        fitting: { ...pair.fitting, [action.eye]: action.value },
      }));
    case 'setLab':
      return updatePair(draft, action.pairId, (pair) => ({ ...pair, labId: action.labId }));
    case 'addPair': {
      if (draft.pairs.length >= MAX_OPTICAL_JOBS_PER_ORDER) return draft;
      const pair = newPair();
      return {
        ...draft,
        touched: true,
        pairs: [...draft.pairs, pair],
        activePairId: pair.id,
        step: 'prescription',
      };
    }
    case 'removePair': {
      if (draft.pairs.length <= 1) return draft;
      const pairs = draft.pairs.filter((pair) => pair.id !== action.pairId);
      if (pairs.length === draft.pairs.length) return draft;
      return {
        ...draft,
        touched: true,
        pairs,
        activePairId:
          draft.activePairId === action.pairId
            ? (pairs[0]?.id ?? draft.activePairId)
            : draft.activePairId,
      };
    }
    case 'acceptPrices':
      return {
        ...draft,
        pairs: draft.pairs.map((pair) => {
          const seen = (key: string, current: number | null) => action.prices[key] ?? current;
          return {
            ...pair,
            frame: pair.frame
              ? {
                  ...pair.frame,
                  seenPriceMinor: seen(pair.lineKeys.frame, pair.frame.seenPriceMinor),
                }
              : null,
            lenses: {
              right: pair.lenses.right
                ? {
                    ...pair.lenses.right,
                    seenPriceMinor: seen(pair.lineKeys.right, pair.lenses.right.seenPriceMinor),
                  }
                : null,
              left: pair.lenses.left
                ? {
                    ...pair.lenses.left,
                    seenPriceMinor: seen(pair.lineKeys.left, pair.lenses.left.seenPriceMinor),
                  }
                : null,
            },
          };
        }),
      };
    case 'setTenders':
      return { ...draft, touched: true, tenders: action.tenders };
    case 'setCredit':
      return { ...draft, touched: true, credit: action.credit };
  }
}

// ---------------------------------------------------------------------------
// Checks that help, by the server's own rules
// ---------------------------------------------------------------------------

/** Millimetres with at most one decimal, as the server reads a fitting height or a PD: a second decimal is refused, never rounded. */
const MILLIMETRES = /^\d{1,3}(\.\d)?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export const prescriptionField = (eye: EyeSide, field: EyeField) => `rx.${eye}.${field}`;

/** What is wrong with a typed external prescription, keyed by field. The server decides; this only saves a round trip. */
export function externalProblems(form: ExternalPrescriptionForm): FormErrors {
  const errors: FormErrors = {};
  for (const eye of ['right', 'left'] as const) {
    validateEye(form[eye], (field) => prescriptionField(eye, field), errors);
    if (form[eye].sphere.trim() === '' && !errors[prescriptionField(eye, 'sphere')]) {
      errors[prescriptionField(eye, 'sphere')] = { key: 'opticalOrders.validation.sphereRequired' };
    }
  }

  const tenths = (value: string, field: 'totalPdMm' | 'rightPdMm' | 'leftPdMm') => {
    const trimmed = value.trim();
    if (trimmed === '') {
      errors[`rx.${field}`] = { key: 'opticalOrders.validation.pdRequired' };
      return null;
    }
    const parsed = MILLIMETRES.test(trimmed) ? parsePdToTenths(trimmed) : null;
    if (parsed === null || parsed <= 0) {
      errors[`rx.${field}`] = { key: 'opticalOrders.validation.millimetres' };
      return null;
    }
    return parsed;
  };

  const triple =
    form.pdMode === 'BINOCULAR'
      ? { totalTenths: tenths(form.totalPdMm, 'totalPdMm'), rightTenths: null, leftTenths: null }
      : {
          totalTenths: null,
          rightTenths: tenths(form.rightPdMm, 'rightPdMm'),
          leftTenths: tenths(form.leftPdMm, 'leftPdMm'),
        };
  for (const problem of checkPdCoherence(triple)) {
    const range = problem.field === 'totalPdMm' ? TOTAL_PD_RANGE_TENTHS : MONOCULAR_PD_RANGE_TENTHS;
    errors[`rx.${problem.field}`] = {
      key: 'opticalOrders.validation.pdRange',
      values: { min: (range.min / 10).toFixed(1), max: (range.max / 10).toFixed(1) },
    };
  }

  if (!form.pdType) errors['rx.pdType'] = { key: 'opticalOrders.validation.pdType' };

  for (const field of ['prescribedOn', 'expiresOn'] as const) {
    const value = form[field].trim();
    if (value !== '' && !DATE.test(value))
      errors[`rx.${field}`] = { key: 'opticalOrders.validation.date' };
  }
  if (
    !errors['rx.prescribedOn'] &&
    !errors['rx.expiresOn'] &&
    form.prescribedOn &&
    form.expiresOn &&
    form.expiresOn < form.prescribedOn
  ) {
    errors['rx.expiresOn'] = { key: 'opticalOrders.validation.expiresBeforeWritten' };
  }

  return errors;
}

/** The form fields that are the PD itself; editing any of them makes the PD typed by hand. */
const PD_VALUE_FIELDS = ['totalPdMm', 'rightPdMm', 'leftPdMm', 'pdMode'] as const;

/**
 * The customer's latest authoritative PD of one type, or null.
 *
 * Authority is the shared rule (`isPrescribablePd`): active, with a PD, never a reference-only estimate and never validation data. Latest is by when it was measured, the order the measurement list already uses.
 */
export function latestAuthoritativePd(
  measurements: readonly CustomerMeasurementSummary[],
  customerId: string,
  pdType: PdType | '',
): CustomerMeasurementSummary | null {
  if (!pdType) return null;
  const eligible = measurements.filter(
    (entry) =>
      entry.customerId === customerId &&
      entry.pdType === pdType &&
      isPrescribablePd(entry) &&
      savedPdFields(entry) !== null,
  );
  return eligible.reduce<CustomerMeasurementSummary | null>(
    (latest, entry) => (latest === null || entry.measuredAt > latest.measuredAt ? entry : latest),
    null,
  );
}

/** A measurement's PD as the form takes it, in the form it was recorded: both eyes when it has them, otherwise its total. Never converted from one to the other. */
export function savedPdFields(
  measurement: CustomerMeasurementSummary,
): Partial<ExternalPrescriptionForm> | null {
  if (!measurement.pdType) return null;
  const source: SavedPdSource = {
    measurementId: measurement.id,
    measuredAt: measurement.measuredAt,
    source: measurement.source,
  };
  if (measurement.rightPdMm && measurement.leftPdMm) {
    return {
      pdMode: 'MONOCULAR',
      totalPdMm: '',
      rightPdMm: measurement.rightPdMm,
      leftPdMm: measurement.leftPdMm,
      pdType: measurement.pdType,
      pdSource: source,
      pdManual: false,
    };
  }
  if (measurement.totalPdMm) {
    return {
      pdMode: 'BINOCULAR',
      totalPdMm: measurement.totalPdMm,
      rightPdMm: '',
      leftPdMm: '',
      pdType: measurement.pdType,
      pdSource: source,
      pdManual: false,
    };
  }
  return null;
}

/** Whether the form's PD boxes are all empty. */
export function pdEmpty(form: ExternalPrescriptionForm): boolean {
  return [form.totalPdMm, form.rightPdMm, form.leftPdMm].every((value) => value.trim() === '');
}

/** Whether the pair is choosing one lens for both eyes: switched on, and the eyes do not already hold different lenses. */
export function sameLensActive(pair: PairDraft): boolean {
  return (
    pair.sameLens &&
    (pair.lenses.right?.variantId ?? null) === (pair.lenses.left?.variantId ?? null)
  );
}

/** Why a stocked lens cannot serve both eyes from this branch: both eyes need a piece each. A made-to-order lens holds no stock and is never refused here; the server's count at confirmation stays the authority. */
export function sharedLensStockProblem(
  supplyMode: PrescriptionLensDetails['supplyMode'],
  availableQuantity: number,
): FieldError | null {
  if (supplyMode !== 'STOCKED') return null;
  if (availableQuantity >= 2) return null;
  return availableQuantity === 1
    ? { key: 'opticalOrders.lens.sharedNeedsTwoOne' }
    : { key: 'opticalOrders.lens.sharedNeedsTwoNone' };
}

/** Whether an eye's lens design needs a fitting height. The same shared rule the server applies. */
export function fittingRequired(lens: SelectedLens | null): boolean {
  return lens ? isMultifocalLensType(lens.details.lensType) : false;
}

/** A fitting height as typed, checked as the server reads it. */
export function fittingProblem(value: string, required: boolean): FieldError | null {
  const trimmed = value.trim();
  if (trimmed === '') return required ? { key: 'opticalOrders.validation.fittingRequired' } : null;
  if (!MILLIMETRES.test(trimmed)) return { key: 'opticalOrders.validation.fittingFormat' };
  const tenths = parsePdToTenths(trimmed);
  if (tenths === null || tenths <= 0) return { key: 'opticalOrders.validation.fittingPositive' };
  return null;
}

export function prescriptionComplete(prescription: PairPrescription): boolean {
  if (prescription.source === 'CLINICAL') return Boolean(prescription.clinicalPrescriptionId);
  if (prescription.source === 'EXTERNAL') {
    return Object.keys(externalProblems(prescription.external)).length === 0;
  }
  return false;
}

export function lensesComplete(pair: PairDraft): boolean {
  const { right, left } = pair.lenses;
  if (!right || !left) return false;
  return (
    fittingProblem(pair.fitting.right, fittingRequired(right)) === null &&
    fittingProblem(pair.fitting.left, fittingRequired(left)) === null
  );
}

export function stepComplete(pair: PairDraft, step: BuilderStep): boolean {
  if (step === 'prescription') return prescriptionComplete(pair.prescription);
  if (step === 'frame') return pair.frame !== null;
  if (step === 'lenses') return lensesComplete(pair);
  return false;
}

export function pairComplete(pair: PairDraft): boolean {
  return PAIR_STEPS.every((step) => stepComplete(pair, step));
}

/** The furthest step the user may open: every pair must be whole before the review. */
export function reachable(draft: BuilderDraft, step: BuilderStep): boolean {
  if (PAIR_STEPS.includes(step)) return true;
  const allComplete = draft.pairs.every(pairComplete);
  return allComplete;
}

// ---------------------------------------------------------------------------
// What is sent
// ---------------------------------------------------------------------------

const trimmedOrNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/** Every field of the eye, always: a blank box travels as null, which the server stores as no value — never as zero. */
function eyeInput(eye: EyeForm): EyePrescriptionInput {
  const axis = eye.axis.trim();
  return {
    sphere: trimmedOrNull(eye.sphere),
    cylinder: trimmedOrNull(eye.cylinder),
    axis: axis === '' ? null : Number(axis),
    add: trimmedOrNull(eye.add),
    prism: trimmedOrNull(eye.prism),
    prismBase: eye.prismBase || null,
  };
}

/** The prescription as the checkout takes it: a clinical one by its id alone, an external one typed in full. */
export function prescriptionInput(prescription: PairPrescription): OpticalPrescriptionInput | null {
  if (prescription.source === 'CLINICAL') {
    return prescription.clinicalPrescriptionId
      ? {
          source: 'CLINICAL_PRESCRIPTION',
          clinicalPrescriptionId: prescription.clinicalPrescriptionId,
        }
      : null;
  }
  if (prescription.source !== 'EXTERNAL') return null;

  const form = prescription.external;
  const optional = (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  };
  return {
    source: 'EXTERNAL_PRESCRIPTION',
    right: eyeInput(form.right),
    left: eyeInput(form.left),
    ...(form.pdType ? { pdType: form.pdType } : {}),
    // Exactly one form of PD travels: the other is not sent at all.
    ...(form.pdMode === 'BINOCULAR'
      ? { totalPdMm: form.totalPdMm.trim() }
      : { rightPdMm: form.rightPdMm.trim(), leftPdMm: form.leftPdMm.trim() }),
    ...(optional(form.externalReference)
      ? { externalReference: optional(form.externalReference) }
      : {}),
    ...(optional(form.prescriberName) ? { prescriberName: optional(form.prescriberName) } : {}),
    ...(optional(form.prescribedOn) ? { prescribedOn: optional(form.prescribedOn) } : {}),
    ...(optional(form.expiresOn) ? { expiresOn: optional(form.expiresOn) } : {}),
    ...(form.pdSource ? { sourceMeasurementId: form.pdSource.measurementId } : {}),
  };
}

export interface PairPayload {
  lines: (CounterSaleLine & { lineKey: string; expectedUnitPriceMinor?: number })[];
  job: OpticalJobInput;
}

/** One whole pair as the checkout takes it: three lines of one each, named by key, and the job naming them. Null while the pair is not whole. */
export function pairPayload(pair: PairDraft): PairPayload | null {
  const prescription = prescriptionInput(pair.prescription);
  const { frame } = pair;
  const { right, left } = pair.lenses;
  if (!prescription || !frame || !right || !left || !pairComplete(pair)) return null;

  const fitting = (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  };
  const rightFitting = fitting(pair.fitting.right);
  const leftFitting = fitting(pair.fitting.left);

  return {
    lines: [
      {
        lineKey: pair.lineKeys.frame,
        variantId: frame.variantId,
        quantity: 1,
        ...(frame.recommendationToken ? { recommendationToken: frame.recommendationToken } : {}),
      },
      { lineKey: pair.lineKeys.right, variantId: right.variantId, quantity: 1 },
      { lineKey: pair.lineKeys.left, variantId: left.variantId, quantity: 1 },
    ],
    job: {
      frameLineKey: pair.lineKeys.frame,
      rightLensLineKey: pair.lineKeys.right,
      leftLensLineKey: pair.lineKeys.left,
      prescription,
      ...(rightFitting ? { rightFittingHeightMm: rightFitting } : {}),
      ...(leftFitting ? { leftFittingHeightMm: leftFitting } : {}),
      ...(pair.labId ? { labId: pair.labId } : {}),
    },
  };
}

/** The price each line was shown at, by line key: what the server is asked to confirm. */
export function seenPrices(pair: PairDraft): Record<string, number> {
  const prices: Record<string, number> = {};
  if (pair.frame?.seenPriceMinor != null) prices[pair.lineKeys.frame] = pair.frame.seenPriceMinor;
  if (pair.lenses.right?.seenPriceMinor != null) {
    prices[pair.lineKeys.right] = pair.lenses.right.seenPriceMinor;
  }
  if (pair.lenses.left?.seenPriceMinor != null) {
    prices[pair.lineKeys.left] = pair.lenses.left.seenPriceMinor;
  }
  return prices;
}

export interface ValidationRequest {
  payload: ValidateBasketPayload;
  /** Which pair each `opticalJobs[i]` of the request is, so a refusal can be shown on it. */
  pairIds: string[];
}

/** The server check of every whole pair, with the prices the screen showed as expectations. Null while no pair is whole. */
export function validationRequest(
  pairs: readonly PairDraft[],
  customerId: string,
  financedMinor = 0,
): ValidationRequest | null {
  const whole = pairs.flatMap((pair) => {
    const payload = pairPayload(pair);
    return payload ? [{ pair, payload }] : [];
  });
  if (whole.length === 0) return null;

  return {
    pairIds: whole.map(({ pair }) => pair.id),
    payload: {
      customerId,
      lines: whole.flatMap(({ pair, payload }) => {
        const prices = seenPrices(pair);
        // The token belongs to the checkout alone; the price check has no field for it.
        return payload.lines.map(({ lineKey, variantId, quantity }) => ({
          lineKey,
          variantId,
          quantity,
          ...(prices[lineKey] != null ? { expectedUnitPriceMinor: prices[lineKey] } : {}),
        }));
      }),
      opticalJobs: whole.map(({ payload }) => payload.job),
      ...(financedMinor > 0 ? { financedMinor } : {}),
    },
  };
}

/** The confirmation, in exactly the checkout's shape. Built only from whole pairs; nothing in it states a price, a job reference or how a line is fulfilled. */
export function checkoutPayload(
  draft: BuilderDraft,
  customerId: string,
): CounterSalePayload | null {
  const pairs = draft.pairs.map(pairPayload);
  if (pairs.length === 0 || pairs.some((pair) => pair === null)) return null;
  const whole = pairs as PairPayload[];
  const tenders = draft.tenders.filter((tender) => tender.amountMinor > 0);

  return {
    customerId,
    lines: whole.flatMap((pair) => pair.lines),
    opticalJobs: whole.map((pair) => pair.job),
    ...(tenders.length > 0 ? { tenders } : {}),
    ...(draft.credit ? { credit: creditTermsInput(draft.credit) } : {}),
  };
}

/** The pairs of the draft whose chosen clinical prescription is not in a list of usable ones. */
export function pairsWithUnusableClinical(
  draft: BuilderDraft,
  usableIds: readonly string[],
): string[] {
  return draft.pairs
    .filter(
      (pair) =>
        pair.prescription.source === 'CLINICAL' &&
        pair.prescription.clinicalPrescriptionId !== null &&
        !usableIds.includes(pair.prescription.clinicalPrescriptionId),
    )
    .map((pair) => pair.id);
}
